/**
 * Answer — the input that captures the player's typed answer.
 *
 * Appears beneath the clue when phase === 'answer'. Free-text input with
 * generous matching (see answer-matcher.ts) — case-insensitive, tolerant
 * of "what is" / "who is" prefix, articles, trailing punctuation.
 *
 * Two buttons: Submit (the primary) and Pass (move on without answering).
 * No countdown timer. Voice-to-text on tablets works natively because
 * `<input type="text">` honors the OS keyboard's mic button.
 */
import { motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { formatDollars, type BoardTileState } from '@/games/categories/engine';

import './Answer.css';

export interface AnswerProps {
  tile: BoardTileState;
  wager: number;
  onSubmit: (text: string) => void;
  onPass: () => void;
}

export function Answer({ tile, wager, onSubmit, onPass }: AnswerProps): React.JSX.Element {
  const [text, setText] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Focus the input on mount.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      const trimmed = text.trim();
      if (!trimmed) return;
      onSubmit(trimmed);
    },
    [text, onSubmit],
  );

  return (
    <motion.section
      className="tb-answer"
      initial={{ opacity: 0, x: '-50%', y: 24 }}
      animate={{ opacity: 1, x: '-50%', y: 0 }}
      transition={{ duration: 0.34, ease: [0.33, 1, 0.68, 1] }}
      aria-label="Your answer"
    >
      <form className="tb-answer__form" onSubmit={handleSubmit} autoComplete="off">
        <label htmlFor="tb-answer-input" className="tb-answer__label">
          Type your answer
          <span className="tb-answer__stake">
            {tile.dailyDouble
              ? `Wagering ${formatDollars(wager)}`
              : `${formatDollars(tile.value)} on the line`}
          </span>
        </label>
        <div className="tb-answer__input-wrap">
          <span className="tb-answer__prefix" aria-hidden="true">
            What is
          </span>
          <input
            id="tb-answer-input"
            ref={inputRef}
            className="tb-answer__input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="…?"
            autoComplete="off"
            autoCapitalize="words"
            spellCheck
            inputMode="text"
            data-testid="answer-input"
          />
        </div>
        <div className="tb-answer__actions">
          <button
            type="submit"
            className="tb-answer__submit"
            disabled={text.trim().length === 0}
            data-testid="answer-submit"
          >
            Submit answer
          </button>
          <button
            type="button"
            className="tb-answer__pass"
            onClick={onPass}
            data-testid="answer-pass"
          >
            Pass
          </button>
        </div>
      </form>
    </motion.section>
  );
}
